import React, { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import UserAvatar from "../../components/ui/icons/UserAvatar";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import { logoutUser } from "../../features/auth/store/login/thunk";
import { useProfile } from "../../features/auth/hooks/useProfile";
import useOrganization from "../../features/organization/hooks/useOrganization";
import DeptTeamSelector, {
  DeptTeamSelectorMode,
} from "../../components/common/DeptTeamSelector/DeptTeamSelector";
import useSurvey from "../../features/survey/hooks/useSurvey";
import { useTeam } from "../../features/TeamcompositeResults/contexts/TeamContext";
import OrgSelector from "../../components/common/OrgSelector/OrgSelector";

export type SidebarTab =
  | "locker-room"
  | "individual-results"
  | "account-settings";

interface TabItem {
  id: SidebarTab;
  text: string;
  href: string;
}

// Roles that see ALL departments (full org structure)
const ORG_LEVEL_ROLES = ["ORG_LEAD"];

// Roles that see only their assigned departments
const DEPT_LEVEL_ROLES = ["DEPT_LEAD"];

const SidebarUser: React.FC<{
  activeTeamName?: string | null;
  allTeamNames?: string[];
}> = ({ activeTeamName, allTeamNames = [] }) => {
  const { user } = useProfile();
  const navigate = useNavigate();

  const BADGE_COLORS = [
    "bg-[#5d4cc6] text-white",
    "bg-[#3b519c] text-white",
    "bg-[#8DE2B0] text-dark-blue",
    "bg-[#f0f7fb] text-dark-blue",
  ];

  const badgesToShow = activeTeamName ? [activeTeamName] : allTeamNames;

  return (
    <div className="text-center px-10 mb-2">
      <div
        className="mx-auto mb-[6px] cursor-pointer"
        onClick={() => navigate("/account-settings")}
      >
        <UserAvatar
          src={user?.image ?? undefined}
          className="w-[88px] h-[88px] mx-auto"
        />
      </div>
      <div
        className="text-[24px] text-white mb-2 font-primary font-medium cursor-pointer"
        onClick={() => navigate("/account-settings")}
      >
        {user?.first_name} {user?.last_name}
      </div>
      <p
        className="block overflow-hidden whitespace-nowrap text-ellipsis text-[18px] font-medium text-white font-primary no-underline hover:text-white/90 cursor-pointer"
        onClick={() => navigate("/account-settings")}
      >
        {user?.email}
      </p>
      {badgesToShow.length > 0 && (
        <div className="flex flex-wrap justify-center gap-2 mt-2">
          {badgesToShow.map((name, i) => (
            <span
              key={name}
              className={`text-[11px] font-primary font-semibold px-3 py-1 rounded-full ${
                BADGE_COLORS[i % BADGE_COLORS.length]
              }`}
            >
              {name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

interface SidebarProps {
  activeTab?: SidebarTab;
  onTabChange?: (tab: SidebarTab) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ activeTab, onTabChange }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentOrganization } = useOrganization();
  const { roles, teams, departmentsLeading, teamsLeading, isCoach } =
    useProfile();

  const [activeTeamName, setActiveTeamName] = useState<string | null>(null);
  const [hasUserSelected, setHasUserSelected] = useState(false);
  const { isCompleted } = useSurvey();
  const { setSelectedTeam } = useTeam();

  //  Determine selector mode based on roles
  const selectorMode = useMemo((): DeptTeamSelectorMode => {
    const roleCodes = roles.map((r: any) => r.code);

    // Highest priority first
    if (roleCodes.some((code: string) => ORG_LEVEL_ROLES.includes(code))) {
      return "org";
    }
    if (roleCodes.some((code: string) => DEPT_LEVEL_ROLES.includes(code))) {
      return "dept_lead";
    }
    return "user";
  }, [roles]);

  const TAB_ITEMS: TabItem[] = [
    { id: "locker-room", text: "Locker Room", href: "/locker-room" },
    {
      id: "account-settings",
      text: "Account Settings",
      href: "/account-settings",
    },
  ];

  if (isCompleted) {
    TAB_ITEMS.splice(1, 0, {
      id: "individual-results",
      text: "Results",
      href: "/individual-results",
    });
  }

  const resolvedActive =
    activeTab ??
    TAB_ITEMS.find((t) => location.pathname.startsWith(t.href))?.id;

  const handleTabClick = (tab: TabItem) => {
    if (onTabChange) onTabChange(tab.id);
    else navigate(tab.href);
  };

  const handleTeamChange = useCallback(
    (team: { id: number; name: string }, isUserAction = false) => {
      if (isUserAction) {
        setActiveTeamName(team.name);
        setHasUserSelected(true);
      }
    },
    [],
  );

  const handleDeptTeamChange = useCallback(
    (team: { id: number; name: string }, isUserAction = false) => {
      handleTeamChange(team, isUserAction);
      setSelectedTeam(team);
    },
    [handleTeamChange, setSelectedTeam],
  );

  const deptTeamSelector = useMemo(() => {
    if (selectorMode === "org" && !currentOrganization?.id) return null;

    if (selectorMode === "dept_lead" && departmentsLeading.length === 0)
      return null;

    const roleCodes = roles.map((r: any) => r.code);
    const isTeamLead = roleCodes.includes("TEAM_LEAD");

    return (
      <DeptTeamSelector
        organizationId={currentOrganization?.id ?? 0}
        onTeamChange={handleDeptTeamChange}
        teamsLeading={teamsLeading}
        mode={selectorMode}
        isTeamLead={isTeamLead}
        allowedDepartments={
          selectorMode === "dept_lead" ? departmentsLeading : undefined
        }
        userTeams={
          selectorMode === "user"
            ? (() => {
                const roleCodes = roles.map((r: any) => r.code);
                const source = roleCodes.includes("TEAM_LEAD")
                  ? teamsLeading
                  : teams;
                return source.filter(
                  (t, index, self) =>
                    self.findIndex((x) => x.id === t.id) === index,
                );
              })()
            : undefined
        }
      />
    );
  }, [
    selectorMode,
    currentOrganization?.id,
    handleDeptTeamChange,
    departmentsLeading,
    teams,
    teamsLeading,
    roles,
  ]);

  const allTeamNames = useMemo(() => {
    return teams
      .filter(
        (t, index, self) => self.findIndex((x) => x.id === t.id) === index,
      )
      .map((t) => t.name);
  }, [teams]);

  const badgeTeamName = hasUserSelected ? activeTeamName : null;

  return (
    <nav className="hidden sidebar:flex flex-col w-[298px] flex-shrink-0 h-[calc(100vh-82px)] bg-dark-blue text-white pt-[60px] pb-[90px] sidebar-scrollbar">
      <SidebarUser activeTeamName={badgeTeamName} allTeamNames={allTeamNames} />

      <ul className="list-none m-0 pt-10 pl-10 flex-shrink-0">
        {TAB_ITEMS.map((item) => {
          const isActive = resolvedActive === item.id;
          return (
            <li key={item.id}>
              <button
                onClick={() => handleTabClick(item)}
                className={`w-full text-left block px-[25px] py-[17px]
                  rounded-[10px_0_0_10px] border-none cursor-pointer
                  font-primary font-semibold
                  transition-all duration-200 text-[18px] ${
                    isActive
                      ? "bg-[#f0f7fb] text-dark-blue font-semibold"
                      : "bg-transparent text-white font-normal hover:bg-white/10"
                  }`}
              >
                {item.text}
              </button>
            </li>
          );
        })}
      </ul>

      <div className="mt-auto pt-10 px-10">
        {isCoach && (
          <div className="mb-4 -mx-2 w-[calc(100%+1rem)] max-w-none">
            <OrgSelector />
          </div>
        )}
        <div
          className="mb-[50px] cursor-pointer -mx-2 w-[calc(100%+1rem)] max-w-none"
          // onClick={() => navigate("/user-management")}
        >
          {deptTeamSelector}
        </div>
        <button onClick={() => dispatch(logoutUser())} className="button-main">
          Log Out
        </button>
      </div>
    </nav>
  );
};

export default Sidebar;
